import { postingSourceSchema } from "@clipboard-health/contract-backend-main";
import { API_TYPES } from "@src/appV2/api/apiTypes";
import { z } from "zod";

import { ShiftSpecificationCategory, TIME_SLOTS } from "../types";

export const baseScheduleSchema = z.object({
  startAt: z.string().datetime(),
  endAt: z.string().datetime(),
  timeSlot: z.enum(TIME_SLOTS),
  extendBookabilityTo: z.string().datetime().optional(),
  durationWithoutBreaksInHours: z.number(),
});

export const baseRequirementsSchema = z.object({
  qualificationName: z.string(),
});

export const baseChargesSchema = z.object({
  total: z.number(),
  rushFeeDifferential: z.number().optional(),
});

export const basePaySchema = z.object({
  total: z.number(),
});

export const baseSpecificationsSchema = z.array(
  z.object({
    category: z.nativeEnum(ShiftSpecificationCategory),
    requirement: z.string(),
  })
);

export const baseRelationshipsSchema = z.object({
  workplace: z.object({
    data: z.object({
      id: z.string(),
      type: z.literal(API_TYPES.workplace),
    }),
  }),
});

// TODO (TFR-226): replace with backend contract imports
const orientationShiftTypeRequestSchema = z.literal("single-shift").optional();
const orientationShiftTypeResponseSchema = z.enum(["single-shift", "block-shifts"]).optional();
export type OrientationShiftType = z.infer<typeof orientationShiftTypeRequestSchema>;

export const createRequestAttributesSchema = z.object({
  schedule: baseScheduleSchema
    .omit({ durationWithoutBreaksInHours: true })
    .refine(({ startAt, endAt }) => new Date(startAt) < new Date(endAt), {
      message: "Start time must be before end time",
      path: ["startAt"],
    }),
  requirements: baseRequirementsSchema,
  charges: z
    .object({
      overrideCharges: z.record(z.string(), z.number()).optional(),
    })
    .optional(),
  specifications: baseSpecificationsSchema.optional(),
  isInstantFill: z.boolean().optional(),
  isRequestToBook: z.boolean().optional(),
  genderRequirement: z.enum(["female", "male"]).optional(),
  orientationShiftType: orientationShiftTypeRequestSchema,
  postingSource: postingSourceSchema,
});

export const createResponseAttributesSchema = z.object({
  schedule: baseScheduleSchema,
  requirements: baseRequirementsSchema,
  charges: baseChargesSchema,
  pay: basePaySchema,
  createdAt: z.string().datetime(),
  orientationShiftType: orientationShiftTypeResponseSchema,
});

export const getResponseAttributesSchema = z.object({
  schedule: baseScheduleSchema.partial().optional(),
  requirements: baseRequirementsSchema.partial().optional(),
  charges: baseChargesSchema.partial().optional(),
  pay: basePaySchema.partial().optional(),
  refillInfo: z
    .object({
      urgencyLevel: z.number().optional(),
      previousWorker: z
        .object({
          userId: z.string(),
          name: z.string(),
        })
        .optional(),
      isUrgentlyBooked: z.boolean().optional(),
    })
    .partial()
    .optional(),
  clockInOut: z
    .object({
      start: z.string().optional(),
      end: z.string().optional(),
    })
    .optional(),
  lunchInOut: z
    .object({
      start: z.string().optional(),
      end: z.string().optional(),
    })
    .optional(),
  postedBy: z
    .object({
      userId: z.string().optional(),
      name: z.string().optional(),
      userType: z.string().optional(),
    })
    .optional(),
  note: z.string().optional(),
  attendance: z
    .object({
      arrivedFacilityGeofenceAt: z.string().optional(),
      leftFacilityGeofenceAt: z.string().optional(),
    })
    .optional(),
  createdAt: z.string().optional(),
});

export const baseShiftDataSchema = z.object({
  id: z.string(),
  type: z.literal(API_TYPES.shift),
});

export type BaseSchedule = z.infer<typeof baseScheduleSchema>;
export type BaseRequirements = z.infer<typeof baseRequirementsSchema>;
export type BaseCharges = z.infer<typeof baseChargesSchema>;
export type BasePay = z.infer<typeof basePaySchema>;
